import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import Card from '../../../components/common/Card';
import PresentationsPageHeader from '../../../components/presentationList/PresentationsPageHeader';
import DataView, {ViewMode} from '../../../components/common/data/DataView';
import Button from '../../../../incase-domain/components/button/Button';
import ConfirmModal from '../../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import {loggedInUserSelector} from '../../../../store/selectors/auth.selectors';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {
    ArchivedPresentation,
    fetchArchivedPresentations,
    restoreArchivedPresentation,
} from '../../../../service/archivedPresentations.service';

const PAGE_SIZE = 20;

function EditorArchivedPresentationsPage(): JSX.Element {
    const {t} = useTranslation();
    const user = useSelector(loggedInUserSelector);
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [selectedForRestore, setSelectedForRestore] = useState<ArchivedPresentation | null>(null);
    const {data, isFetching, isError} = useQuery(
        QUERY_KEYS.archivedPresentation.list({userId: user?._id, search, page}),
        () => fetchArchivedPresentations(search, page, PAGE_SIZE),
        {enabled: !!user?._id}
    );

    useEffect(() => {
        if (data && page > 1 && data.items.length === 0) {
            setPage(Math.max(1, Math.ceil(data.total / PAGE_SIZE)));
        }
    }, [data, page]);

    const mutation = useMutation(restoreArchivedPresentation, {
        onSuccess: () => {
            toast.success(t('archivedPresentations.restored'));
            queryClient.invalidateQueries({queryKey: QUERY_KEYS.archivedPresentation.lists()});
            queryClient.invalidateQueries({queryKey: QUERY_KEYS.presentation.lists()});
            queryClient.invalidateQueries({queryKey: QUERY_KEYS.presentationFolder.lists()});
        },
        onError: () => toast.error(t('archivedPresentations.restoreFailed')),
    });

    const renderPresentation = (row: ArchivedPresentation): JSX.Element => <>{row.name}</>;

    const renderId = (row: ArchivedPresentation): JSX.Element => <>{row._id}</>;

    const renderActions = (row: ArchivedPresentation): JSX.Element => (
        <Button
            size="sm"
            variant="outline"
            color="secondary"
            disabled={mutation.isLoading || isFetching}
            loading={mutation.isLoading && mutation.variables === row._id}
            onClick={() => setSelectedForRestore(row)}
        >
            {t('archivedPresentations.restore')}
        </Button>
    );

    return (
        <>
            <PresentationsPageHeader view="archived" />
            <Card>
                <p className="u-mb-2">{t('archivedPresentations.searchHelp')}</p>
                {isError && <p role="alert">{t('archivedPresentations.loadFailed')}</p>}
                <DataView
                    title={t('archivedPresentations.title')}
                    inlineTitle
                    enableSearch
                    search={search}
                    onSearchChange={(value) => {
                        setSearch(value);
                        setPage(1);
                    }}
                    rows={data?.items ?? []}
                    rowIdentifier={(row) => row._id}
                    loading={isFetching}
                    paginationData={{currentPage: page, pageSize: PAGE_SIZE, itemsCount: data?.total ?? 0}}
                    onPageChange={setPage}
                    views={[
                        {
                            view: ViewMode.TABLE,
                            columns: [
                                {label: t('archivedPresentations.presentation'), body: renderPresentation},
                                {label: t('archivedPresentations.id'), body: renderId},
                                {body: renderActions, align: 'right', width: '1%'},
                            ],
                        },
                    ]}
                />
            </Card>
            <ConfirmModal
                isOpen={selectedForRestore !== null}
                message={t('archivedPresentations.confirmRestore', {name: selectedForRestore?.name})}
                confirmText={t('archivedPresentations.restore')}
                onCancel={() => setSelectedForRestore(null)}
                onConfirm={() => {
                    if (selectedForRestore && !mutation.isLoading) mutation.mutate(selectedForRestore._id);
                    setSelectedForRestore(null);
                }}
            />
        </>
    );
}

export default EditorArchivedPresentationsPage;
